// Pure date + layout helpers shared by the Calendar view and the Planner page.

import { addDays, addMonths, addWeeks, differenceInCalendarDays, format, isSameDay, isSameMonth, isSameYear, startOfDay, startOfWeek } from 'date-fns';
import type { Location, Task } from '@shared/types';
import type { Tables } from '@/lib/store';
import { getDb } from '@/lib/store';
import { createTask, updateTask } from '@/lib/actions';
import { toast } from '@/components/ui/overlays';

export type CalendarMode = 'month' | 'week' | 'day';

/** chip height, and one lane = chip + vertical gap (month rows and the all-day lane) */
export const CHIP_H = 30;
export const LANE_H = 35;
/** pixel height of one hour in the week / day time grid */
export const HOUR_H = 48;
/** default length of a timed task without a time estimate (minutes) */
export const DEFAULT_TIMED_MIN = 30;

export const dayOf = (ts: number) => startOfDay(ts).getTime();

/** Local time `minutes` after midnight of `day` (DST safe). */
export function atMinutes(day: number, minutes: number) {
  const d = new Date(day);
  d.setHours(0, minutes, 0, 0);
  return d.getTime();
}

export function minutesOfDay(ts: number) {
  const d = new Date(ts);
  return d.getHours() * 60 + d.getMinutes();
}

export function formatClock(minutes: number) {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  const ampm = h < 12 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m ? `${h12}:${String(m).padStart(2, '0')}${ampm}` : `${h12}${ampm}`;
}

export function hourLabel(h: number) {
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12} ${h < 12 ? 'AM' : 'PM'}`;
}

// ---------------------------------------------------------------------------
// task date semantics

/** Inclusive day range a task occupies on the calendar (start/due, either may be missing). */
export function taskSpan(t: Task): { start: number; end: number } | null {
  const s = t.startDate ?? t.dueDate;
  const e = t.dueDate ?? t.startDate;
  if (s === null || s === undefined || e === null || e === undefined) return null;
  const a = dayOf(s);
  const b = dayOf(e);
  return a <= b ? { start: a, end: b } : { start: b, end: a };
}

/** Tasks with a due time on a single day are placed at their hour in the time grid. */
export function isTimedTask(t: Task) {
  return !!t.dueTime && t.dueDate !== null && (t.startDate === null || isSameDay(t.startDate, t.dueDate));
}

export function isUnscheduled(t: Task) {
  return t.startDate === null && t.dueDate === null;
}

export function timedDuration(t: Task) {
  const est = t.timeEstimate ? Math.round(t.timeEstimate / 60000) : DEFAULT_TIMED_MIN;
  return Math.max(15, Math.min(24 * 60, est));
}

// ---------------------------------------------------------------------------
// ranges & titles

export function monthGridStart(cursor: number) {
  const first = new Date(cursor);
  first.setDate(1);
  return startOfWeek(first).getTime();
}

/** Weeks the month grid shows: only those containing days of the month (4–6). */
export function monthWeekCount(cursor: number) {
  const first = new Date(cursor);
  first.setDate(1);
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  return Math.ceil((differenceInCalendarDays(first, monthGridStart(cursor)) + daysInMonth) / 7);
}

export function visibleDays(mode: CalendarMode, cursor: number): number[] {
  if (mode === 'day') return [dayOf(cursor)];
  if (mode === 'week') {
    const s = startOfWeek(cursor);
    return Array.from({ length: 7 }, (_, i) => addDays(s, i).getTime());
  }
  const s = monthGridStart(cursor);
  return Array.from({ length: monthWeekCount(cursor) * 7 }, (_, i) => addDays(s, i).getTime());
}

export function shiftCursor(mode: CalendarMode, cursor: number, dir: 1 | -1) {
  if (mode === 'month') {
    const d = new Date(cursor);
    d.setDate(1);
    return addMonths(d, dir).getTime();
  }
  if (mode === 'week') return addWeeks(cursor, dir).getTime();
  return addDays(cursor, dir).getTime();
}

export function rangeTitle(mode: CalendarMode, cursor: number) {
  if (mode === 'month') return format(cursor, 'MMMM yyyy');
  if (mode === 'day') return format(cursor, 'EEEE, MMMM d, yyyy');
  const s = startOfWeek(cursor);
  const e = addDays(s, 6);
  if (!isSameYear(s, e)) return `${format(s, 'MMM d, yyyy')} – ${format(e, 'MMM d, yyyy')}`;
  if (!isSameMonth(s, e)) return `${format(s, 'MMM d')} – ${format(e, 'MMM d, yyyy')}`;
  return `${format(s, 'MMM d')} – ${format(e, 'd, yyyy')}`;
}

// ---------------------------------------------------------------------------
// lane layout for day-spanning chips (month rows and the all-day lane)

export interface Seg {
  key: string;
  task: Task;
  startCol: number;
  endCol: number;
  lane: number;
  /** the task started before this row / continues after it */
  contLeft: boolean;
  contRight: boolean;
  /** first day (ms) of this segment */
  segStart: number;
}

function timeKey(t: Task) {
  return t.dueTime && t.dueDate !== null ? minutesOfDay(t.dueDate) : -1;
}

export function layoutSegments(tasks: Task[], rangeStart: number, nDays: number, keyPrefix: string): Seg[] {
  const rangeEnd = addDays(rangeStart, nDays - 1).getTime();
  const items: Omit<Seg, 'lane' | 'key'>[] = [];
  for (const t of tasks) {
    const sp = taskSpan(t);
    if (!sp || sp.end < rangeStart || sp.start > rangeEnd) continue;
    const s = Math.max(sp.start, rangeStart);
    const e = Math.min(sp.end, rangeEnd);
    items.push({
      task: t,
      startCol: differenceInCalendarDays(s, rangeStart),
      endCol: differenceInCalendarDays(e, rangeStart),
      contLeft: sp.start < rangeStart,
      contRight: sp.end > rangeEnd,
      segStart: s,
    });
  }
  items.sort(
    (a, b) =>
      a.startCol - b.startCol ||
      b.endCol - b.startCol - (a.endCol - a.startCol) ||
      timeKey(a.task) - timeKey(b.task) ||
      a.task.orderIndex - b.task.orderIndex ||
      a.task.name.localeCompare(b.task.name),
  );
  const lanes: boolean[][] = [];
  return items.map((it) => {
    let lane = 0;
    for (;;) {
      const row = (lanes[lane] ??= []);
      let free = true;
      for (let c = it.startCol; c <= it.endCol; c++) if (row[c]) free = false;
      if (free) {
        for (let c = it.startCol; c <= it.endCol; c++) row[c] = true;
        break;
      }
      lane++;
    }
    return { ...it, lane, key: `${keyPrefix}:${it.task.id}:${rangeStart}` };
  });
}

export interface LimitedSegs {
  visible: Seg[];
  /** every segment touching a column, in lane order (for the "+N more" popover) */
  perDay: Seg[][];
  /** hidden count per column */
  more: number[];
  /** lane index where the "+N more" button sits per column */
  moreLane: number[];
  /** lanes actually used (including the "+more" lane) */
  lanesUsed: number;
}

/** Hide segments that don't fit in `maxLanes`, reserving the last lane of overflowing days for "+N more". */
export function limitSegments(segs: Seg[], nDays: number, maxLanes: number): LimitedSegs {
  const perDay: Seg[][] = Array.from({ length: nDays }, () => []);
  for (const s of segs) for (let c = s.startCol; c <= s.endCol; c++) perDay[c].push(s);
  for (const list of perDay) list.sort((a, b) => a.lane - b.lane);
  const max = Math.max(1, maxLanes);
  const limit = perDay.map((list) => (list.length > max ? max - 1 : max));
  let visibleSet = new Set<Seg>();
  for (let iter = 0; iter < nDays + 2; iter++) {
    visibleSet = new Set(
      segs.filter((s) => {
        for (let c = s.startCol; c <= s.endCol; c++) if (s.lane >= limit[c]) return false;
        return true;
      }),
    );
    let changed = false;
    for (let c = 0; c < nDays; c++) {
      if (limit[c] === max && perDay[c].some((s) => !visibleSet.has(s))) {
        limit[c] = max - 1;
        changed = true;
      }
    }
    if (!changed) break;
  }
  const more = perDay.map((list) => list.filter((s) => !visibleSet.has(s)).length);
  let lanesUsed = 0;
  for (const s of visibleSet) lanesUsed = Math.max(lanesUsed, s.lane + 1);
  for (let c = 0; c < nDays; c++) if (more[c] > 0) lanesUsed = Math.max(lanesUsed, limit[c] + 1);
  return { visible: segs.filter((s) => visibleSet.has(s)), perDay, more, moreLane: limit, lanesUsed };
}

// ---------------------------------------------------------------------------
// timed tasks: overlap columns inside a day

export interface TimedItem {
  task: Task;
  startMin: number;
  endMin: number;
  col: number;
  cols: number;
}

export function layoutTimed(tasks: Task[]): TimedItem[] {
  const items: TimedItem[] = tasks
    .map((t) => {
      const startMin = minutesOfDay(t.dueDate!);
      return { task: t, startMin, endMin: Math.min(24 * 60, startMin + Math.max(30, timedDuration(t))), col: 0, cols: 1 };
    })
    .sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin || a.task.orderIndex - b.task.orderIndex);
  let cluster: TimedItem[] = [];
  let colEnds: number[] = [];
  let clusterEnd = -1;
  const flush = () => {
    for (const it of cluster) it.cols = colEnds.length;
    cluster = [];
    colEnds = [];
  };
  for (const it of items) {
    if (it.startMin >= clusterEnd) flush();
    let col = colEnds.findIndex((end) => end <= it.startMin);
    if (col < 0) {
      col = colEnds.length;
      colEnds.push(it.endMin);
    } else colEnds[col] = it.endMin;
    it.col = col;
    cluster.push(it);
    clusterEnd = Math.max(clusterEnd, it.endMin);
  }
  flush();
  return items;
}

// ---------------------------------------------------------------------------
// writes

export type DropTarget =
  | { kind: 'day'; day: number; allDay?: boolean }
  | { kind: 'time'; day: number; minutes: number }
  | { kind: 'unschedule' };

/**
 * Reschedule a task dropped on the calendar. `anchorDay` is the day of the chip the user grabbed
 * (so a multi-day bar moves by the distance it was dragged); start dates keep their distance to the due date.
 */
export function applyDrop(taskId: string, anchorDay: number | null, target: DropTarget) {
  const t = getDb().tasks[taskId];
  if (!t) return;
  const prev: Partial<Task> = { startDate: t.startDate, dueDate: t.dueDate, dueTime: t.dueTime ?? false };
  const changes: Partial<Task> = {};
  if (target.kind === 'unschedule') {
    if (t.startDate === null && t.dueDate === null) return;
    changes.startDate = null;
    changes.dueDate = null;
    changes.dueTime = false;
  } else if (target.kind === 'time') {
    const ref = t.dueDate ?? t.startDate;
    const delta = ref === null ? 0 : differenceInCalendarDays(target.day, ref);
    changes.dueDate = atMinutes(target.day, target.minutes);
    changes.dueTime = true;
    if (t.startDate !== null) {
      const start = addDays(t.startDate, delta).getTime();
      changes.startDate = start > changes.dueDate ? dayOf(changes.dueDate) : start;
    }
  } else if (t.startDate === null && t.dueDate === null) {
    changes.dueDate = target.day;
    changes.dueTime = false;
  } else {
    const anchor = anchorDay ?? dayOf((t.dueDate ?? t.startDate)!);
    const delta = differenceInCalendarDays(target.day, anchor);
    if (t.dueDate !== null) {
      if (target.allDay) {
        changes.dueDate = addDays(dayOf(t.dueDate), delta).getTime();
        changes.dueTime = false;
      } else if (delta !== 0) changes.dueDate = addDays(t.dueDate, delta).getTime();
    }
    if (t.startDate !== null && delta !== 0) changes.startDate = addDays(t.startDate, delta).getTime();
  }
  const changed =
    ('startDate' in changes && changes.startDate !== t.startDate) ||
    ('dueDate' in changes && changes.dueDate !== t.dueDate) ||
    ('dueTime' in changes && !!changes.dueTime !== !!t.dueTime);
  if (!changed) return;
  updateTask(taskId, changes);
  const label =
    target.kind === 'unschedule'
      ? 'Task unscheduled'
      : `Rescheduled to ${format(changes.dueDate ?? changes.startDate ?? target.day, 'MMM d')}${changes.dueTime ? `, ${formatClock(minutesOfDay(changes.dueDate!))}` : ''}`;
  toast(label, { label: 'Undo', onClick: () => updateTask(taskId, prev) });
}

export interface AddTarget {
  day: number;
  /** minutes after midnight; null => all-day */
  minutes: number | null;
}

/** List used for tasks created from a location-level view (first list in the location). */
export function resolveListId(db: Tables, location: Location): string | undefined {
  if (location.type === 'list') return location.id;
  const lists = Object.values(db.lists)
    .filter(
      (l) =>
        !l.archived &&
        (location.type === 'folder' ? l.folderId === location.id : location.type === 'space' ? l.spaceId === location.id : db.spaces[l.spaceId]?.workspaceId === location.id),
    )
    .sort((a, b) => a.orderIndex - b.orderIndex);
  return lists[0]?.id;
}

export function quickCreate(listId: string, name: string, target: AddTarget, assigneeIds: string[] = []) {
  const due = target.minutes === null ? target.day : atMinutes(target.day, target.minutes);
  const task = createTask({ listId, name, dueDate: due, assigneeIds });
  // NewTask has no dueTime field yet; flag the time separately
  if (target.minutes !== null) updateTask(task.id, { dueTime: true });
  return task;
}
